import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader,
  CircularProgress, 
  Alert, 
  Chip,
  Button,
  Paper,
  Skeleton
} from '@mui/material'
import tokenManager from '../utils/tokenManager'
import FeaturedPropertiesCarousel from './FeaturedPropertiesCarousel'
import FeaturedAgentsCarousel from './FeaturedAgentsCarousel'
import FeaturedWidget from './FeaturedWidget'
import RealtorWidget from './RealtorWidget'
import { useContentTypeByEmbed } from '../hooks/useContentByEmbed'
import { useState, useEffect, type ReactNode } from 'react'
import {FeaturedAgentsDetail, PropertyMapWidget, SellAHomeWidget, GetInTouchWidget} from "./index.ts";
import PropertyPage from "./propertyDetail/pages/PropertyPage.tsx";
import {PropertySearchBar} from "./PropertySearchBar";

export interface GenericWidgetProps {
  embedId: string
  title?: string
  subtitle?: string
  description?: string
  variant?: 'card' | 'simple' | 'minimal'
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
  size?: 'small' | 'medium' | 'large'
  showAuthStatus?: boolean
  autoAuth?: boolean
  customContent?: ReactNode
  parameters?: Record<string, any>
  onAuthSuccess?: (token: string) => void
  onAuthError?: (error: string) => void
  onParametersChange?: (parameters: Record<string, any>) => void
  url?: string
}

const GenericWidget = ({
  embedId,
  title = 'Generic Widget',
  subtitle,
  description,
  variant = 'card',
  color = 'primary',
  size = 'medium',
  showAuthStatus = true,
  autoAuth = true,
  customContent,
  parameters = {},
  onAuthSuccess,
  onAuthError,
  onParametersChange,
  url
}: GenericWidgetProps) => {
  console.log('variant',variant)
  console.log('url',url);
  const [authError, setAuthError] = useState<string | null>(null)
  const [widgetParameters] = useState(parameters)
  
  const tokenInfo = tokenManager.getTokenInfo()
  const isAuthenticated = tokenInfo.isValid
  
  // Hook para obtener el tipo de contenido por embedId
  const { 
    data: contentTypeData, 
    isLoading: isLoadingContentType, 
    error: contentTypeError 
  } = useContentTypeByEmbed(embedId)


  useEffect(() => {
    if (embedId) {
      tokenManager.setEmbedId(embedId)
    }
  }, [embedId])

  // Notificar cambios en parámetros
  useEffect(() => {
    if (onParametersChange) {
      onParametersChange(widgetParameters)
    }
  }, [widgetParameters, onParametersChange])


  useEffect(() => {
    if (embedId && autoAuth) {
      const handleTokenRefresh = (token: string) => {
        onAuthSuccess?.(token)
        setAuthError(null)
      }
      
      tokenManager.onTokenRefresh(handleTokenRefresh)
      const currentToken = tokenManager.getCurrentToken()
      if (currentToken && isAuthenticated) {
        onAuthSuccess?.(currentToken)
      }
      
      return () => {
      }
    }
  }, [embedId, autoAuth, isAuthenticated, onAuthSuccess])

  

  const renderParameters = () => {
    if (Object.keys(widgetParameters).length === 0) return null

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Parámetros:
        </Typography>
        <Paper sx={{ p: 1, bgcolor: 'grey.50' }}>
          <pre style={{ 
            fontSize: '0.75rem', 
            margin: 0, 
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {JSON.stringify(widgetParameters, null, 2)}
          </pre>
        </Paper>
      </Box>
    )
  }


  // Función para renderizar componente específico por data.code
  const renderSpecificComponent = () => {
    // Si está cargando el tipo de contenido, mostrar skeleton
    if (isLoadingContentType) {
      return (
        <Box sx={{ width: '100%', p: 2 }}>
          <Skeleton 
            variant="rectangular" 
            width="100%" 
            height={200} 
            animation="wave"
            sx={{ borderRadius: 1 }}
          />
        </Box>
      )
    }
    
    // Si hay error al cargar el tipo de contenido, mostrar error
    if (contentTypeError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          Error al cargar el tipo de widget: {contentTypeError.message}
        </Alert>
      )
    }
    
    // Si terminó de cargar pero no hay datos, retornar null para mostrar error genérico
    // Solo mostrar error si realmente terminó de cargar (no está cargando y no hay error)
    if (!isLoadingContentType && !contentTypeError && !contentTypeData?.data?.code) {
      return null
    }
    
    // Si todavía no hay datos pero está cargando, no hacer nada (ya se maneja arriba)
    if (!contentTypeData?.data?.code) {
      return null
    }
    
    // Usar data.code para determinar qué componente renderizar
    const componentCode = contentTypeData.data.code

    console.log('contentTypeData',contentTypeData)
    console.log('renderSpecificComponent', 'componentCode:', componentCode)
    
    switch (componentCode) {
      case 'FeaturedPropertiesCarousel':
      case 'FeaturedPropertiesInherit':
        return (
            <FeaturedWidget
                title={title}
                embedId={embedId}
            />
        )
      case 'FeaturedPropertiesInheritCarousel':
        return (
          <FeaturedPropertiesCarousel
            title={title}
            embedId={embedId}
          />
        )
      case 'FeaturedAgentsCarousel':
      case 'FeaturedAgentsCarouselLeader':
        return (
          /*<FeaturedAgentsCarousel
            title={title}
            embedId={embedId}
          />*/
            <RealtorWidget
                title={title}
                embedId={embedId}
                dataConfig={contentTypeData.data}
                centerItems={true}
            />
        )
      case 'FeaturedAgentsInherit':
        return (
            <RealtorWidget
                title={title}
                embedId={embedId}
                dataConfig={contentTypeData.data}
            />
        )
      case 'FeaturedPropertiesMap':
        return (
            <PropertyMapWidget
                title={title}
                embedId={embedId}
            />
        )
      case 'FeaturedWidget':
        return (
          <FeaturedWidget
            title={title}
            embedId={embedId}
          />
        )
      case 'RealtorWidget':
        return (
          <RealtorWidget
            title={title}
            embedId={embedId}
            dataConfig={contentTypeData.data}
          />
        )
      case 'FeaturedAgentsDetail':
        return (
          <FeaturedAgentsDetail
            title={title}
            embedId={embedId}
            encriptedString={url}
          />
        )
      case 'FeaturedAgentsDetailLeader':
        return (
          <FeaturedAgentsDetail
            title={title}
            embedId={embedId}
            encriptedString={url}
          />
        )
      case 'FeaturedPropertiesDetail':
        console.log('FeaturedPropertiesDetail contentTypeData',contentTypeData)


        return (
          <PropertyPage
            title={title}
            embedId={embedId}
            encriptedString={url}
            dataConfig={contentTypeData.data}
          />
        )
      case 'PropertySearchBar':
        return (
          <PropertySearchBar
            title={title}
            embedId={embedId}
            encriptedString={url}
          />
        )
      case 'SellAHomeWidget':
      case 'SellAHome':
      case 'FormValuation':
        return (
          <SellAHomeWidget
            embedId={embedId}
          />
        )
      case 'FormGetInForm':
        return (
          <GetInTouchWidget
            embedId={embedId}
            title="Get In Touch"
          />
        )
      // Aquí puedes agregar más casos para otros códigos de componente
      // case 'ProductCatalog':
      //   return <ProductCatalog {...props} />
      // case 'UserDashboard':
      //   return <UserDashboard {...props} />
      default:
        console.log('No se encontró componente específico para el código:', componentCode)
        return null // Si no hay componente específico, usa el genérico
    }
  }


  // Si hay un componente específico para este embedId, usarlo
  const specificComponent = renderSpecificComponent()
  if (specificComponent) {
    return specificComponent
  }

  // Si todavía está cargando, mostrar skeleton o no mostrar nada
  if (isLoadingContentType) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height={200} 
          animation="wave"
          sx={{ borderRadius: 1 }}
        />
      </Box>
    )
  }

  // Si hay error al cargar, mostrar error
  if (contentTypeError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error al cargar el tipo de widget: {contentTypeError.message}
      </Alert>
    )
  }

  // Si terminó de cargar y no hay widget, no mostrar nada (en lugar de mostrar error)
  // Solo retornar null para que no se muestre ningún mensaje de error
  return null
}

export default GenericWidget
